// ⚽ AMICHEVOLE - Componente per registrare partite amichevoli con punteggio ranking
import React, { useState, useEffect, useCallback } from 'react';
import { useConfirmDialog } from './components/ui/ConfirmDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Punti ranking predefiniti per posizione classifica
const DEFAULT_PUNTI_VITTORIA = 3;
const DEFAULT_PUNTI_PAREGGIO = 1;
const DEFAULT_PUNTI_SCONFITTA = 0;

export function PartitaSingola({ token, onRefresh }) {
  const { showConfirm } = useConfirmDialog();
  const [partite, setPartite] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });
  
  // Modal
  const [showNuovaPartita, setShowNuovaPartita] = useState(false);
  const [editingPartita, setEditingPartita] = useState(null);
  
  // Anagrafica soci per select
  const [soci, setSoci] = useState([]);
  
  // Form
  const [form, setForm] = useState({
    giocatore_a_id: '',
    giocatore_a_nome: '',
    giocatore_b_id: '',
    giocatore_b_nome: '',
    squadra_a: '',
    squadra_b: '',
    gol_a: 0,
    gol_b: 0,
    data_partita: new Date().toISOString().split('T')[0],
    punti_ranking_a: DEFAULT_PUNTI_VITTORIA,
    punti_ranking_b: DEFAULT_PUNTI_SCONFITTA,
    note: ''
  });

  const showMsg = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type: '', text: '' }), 4000);
  };

  const api = useCallback(async (endpoint, options = {}) => {
    try {
      const res = await fetch(`${BACKEND_URL}${endpoint}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          ...options.headers
        }
      });
      return res;
    } catch (err) {
      console.error('API Error:', err);
      return null;
    }
  }, [token]);

  // ============ LOAD DATA ============

  const loadPartite = useCallback(async () => {
    setLoading(true);
    const r = await api('/api/clubs/partite-singole');
    if (r?.ok) {
      const data = await r.json();
      setPartite(data.partite || []);
    }
    setLoading(false);
  }, [api]);

  const loadSoci = useCallback(async () => {
    const r = await api('/api/clubs/anagrafica');
    if (r?.ok) {
      const data = await r.json();
      setSoci(data.soci || []);
    }
  }, [api]);

  useEffect(() => {
    const initData = async () => {
      await loadPartite();
      await loadSoci();
    };
    initData();
  }, [loadPartite, loadSoci]);

  // ============ ACTIONS ============

  const resetForm = () => {
    setForm({
      giocatore_a_id: '',
      giocatore_a_nome: '',
      giocatore_b_id: '',
      giocatore_b_nome: '',
      squadra_a: '',
      squadra_b: '',
      gol_a: 0,
      gol_b: 0,
      data_partita: new Date().toISOString().split('T')[0],
      punti_ranking_a: DEFAULT_PUNTI_VITTORIA,
      punti_ranking_b: DEFAULT_PUNTI_SCONFITTA,
      note: ''
    });
  };

  // Auto-calcola punti ranking in base al risultato
  const calcolaPuntiRanking = (golA, golB) => {
    if (golA > golB) {
      return { punti_a: DEFAULT_PUNTI_VITTORIA, punti_b: DEFAULT_PUNTI_SCONFITTA };
    } else if (golB > golA) {
      return { punti_a: DEFAULT_PUNTI_SCONFITTA, punti_b: DEFAULT_PUNTI_VITTORIA };
    } else {
      return { punti_a: DEFAULT_PUNTI_PAREGGIO, punti_b: DEFAULT_PUNTI_PAREGGIO };
    }
  };

  const handleGolChange = (campo, valore) => {
    const nuovoGolA = campo === 'gol_a' ? parseInt(valore) || 0 : form.gol_a;
    const nuovoGolB = campo === 'gol_b' ? parseInt(valore) || 0 : form.gol_b;
    const { punti_a, punti_b } = calcolaPuntiRanking(nuovoGolA, nuovoGolB);
    
    setForm({
      ...form,
      [campo]: parseInt(valore) || 0,
      punti_ranking_a: punti_a,
      punti_ranking_b: punti_b
    });
  };

  const handleGiocatoreChange = (campo, socioId) => {
    const socio = soci.find(s => s.id === socioId);
    const nomeCompleto = socio ? (socio.nome_completo || `${socio.nome} ${socio.cognome}`) : '';
    
    if (campo === 'a') {
      setForm({
        ...form,
        giocatore_a_id: socioId,
        giocatore_a_nome: nomeCompleto
      });
    } else {
      setForm({
        ...form,
        giocatore_b_id: socioId,
        giocatore_b_nome: nomeCompleto
      });
    }
  };

  const savePartita = async () => {
    // Validazione
    if (!form.giocatore_a_nome && !form.giocatore_a_id) {
      showMsg('error', 'Seleziona il giocatore A');
      return;
    }
    if (!form.giocatore_b_nome && !form.giocatore_b_id) {
      showMsg('error', 'Seleziona il giocatore B');
      return;
    }
    if (form.giocatore_a_id === form.giocatore_b_id && form.giocatore_a_id) {
      showMsg('error', 'I due giocatori devono essere diversi');
      return;
    }
    
    setSaving(true);
    
    const url = editingPartita 
      ? `/api/clubs/partite-singole/${editingPartita.id}`
      : '/api/clubs/partite-singole';
    const method = editingPartita ? 'PUT' : 'POST';
    
    const r = await api(url, {
      method,
      body: JSON.stringify(form)
    });
    
    if (r?.ok) {
      showMsg('success', editingPartita ? 'Partita aggiornata' : 'Partita registrata');
      setShowNuovaPartita(false);
      setEditingPartita(null);
      resetForm();
      loadPartite();
      if (onRefresh) onRefresh();
    } else {
      const err = await r?.json();
      showMsg('error', err?.detail || 'Errore salvataggio');
    }
    
    setSaving(false);
  };

  const deletePartita = async (id) => {
    showConfirm({
      title: 'Elimina Partita',
      message: 'Sei sicuro di voler eliminare questa partita?',
      confirmText: '🗑️ Elimina',
      cancelText: 'Annulla',
      variant: 'danger',
      onConfirm: async () => {
        const r = await api(`/api/clubs/partite-singole/${id}`, { method: 'DELETE' });
        if (r?.ok) {
          showMsg('success', 'Partita eliminata');
          loadPartite();
        } else {
          showMsg('error', 'Errore eliminazione');
        }
      }
    });
  };

  const openEdit = (partita) => {
    setForm({
      giocatore_a_id: partita.giocatore_a_id || '',
      giocatore_a_nome: partita.giocatore_a_nome || '',
      giocatore_b_id: partita.giocatore_b_id || '',
      giocatore_b_nome: partita.giocatore_b_nome || '',
      squadra_a: partita.squadra_a || '',
      squadra_b: partita.squadra_b || '',
      gol_a: partita.gol_a || 0,
      gol_b: partita.gol_b || 0,
      data_partita: partita.data_partita || new Date().toISOString().split('T')[0],
      punti_ranking_a: partita.punti_ranking_a ?? DEFAULT_PUNTI_VITTORIA,
      punti_ranking_b: partita.punti_ranking_b ?? DEFAULT_PUNTI_SCONFITTA,
      note: partita.note || ''
    });
    setEditingPartita(partita);
    setShowNuovaPartita(true);
  };

  // ============ HELPERS ============

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('it-IT', { 
        day: '2-digit', 
        month: '2-digit', 
        year: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  const getRisultatoBadge = (golA, golB) => {
    if (golA > golB) {
      return <span className="text-green-600 font-bold">Vittoria A</span>;
    } else if (golB > golA) {
      return <span className="text-blue-600 font-bold">Vittoria B</span>;
    } else {
      return <span className="text-gray-600 font-bold">Pareggio</span>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-10 w-10 border-4 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            ⚽ Amichevoli
          </h2>
          <p className="text-sm text-gray-500">Registra partite fuori torneo con punti ranking</p>
        </div>
        <button
          onClick={() => { setShowNuovaPartita(true); setEditingPartita(null); resetForm(); }}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          data-testid="btn-nuova-partita"
        >
          + Nuova Partita
        </button>
      </div>

      {/* Messages */}
      {msg.text && (
        <div className={`px-4 py-3 rounded-lg ${msg.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
          {msg.text}
        </div>
      )}

      {/* Lista Partite */}
      {partite.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-8 text-center">
          <div className="text-6xl mb-4">⚽</div>
          <h3 className="text-lg font-bold mb-2">Nessuna partita</h3>
          <p className="text-gray-500 mb-4">Registra la tua prima amichevole</p>
          <button 
            onClick={() => { setShowNuovaPartita(true); resetForm(); }}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            Registra Partita
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Giocatore A</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Risultato</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Giocatore B</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Punti Ranking</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {partite.map(p => (
                  <tr key={p.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">{formatDate(p.data_partita)}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{p.giocatore_a_nome}</div>
                      {p.squadra_a && <div className="text-xs text-gray-400">{p.squadra_a}</div>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <span className="text-2xl font-bold text-green-600">{p.gol_a}</span>
                        <span className="text-gray-400">-</span>
                        <span className="text-2xl font-bold text-blue-600">{p.gol_b}</span>
                      </div>
                      <div className="text-xs mt-1">{getRisultatoBadge(p.gol_a, p.gol_b)}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium">{p.giocatore_b_nome}</div>
                      {p.squadra_b && <div className="text-xs text-gray-400">{p.squadra_b}</div>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex justify-center gap-4">
                        <span className="text-green-600 font-bold">A: +{p.punti_ranking_a || 0}</span>
                        <span className="text-blue-600 font-bold">B: +{p.punti_ranking_b || 0}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => openEdit(p)}
                        className="text-blue-600 hover:text-blue-800 px-2"
                        title="Modifica"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => deletePartita(p.id)}
                        className="text-red-600 hover:text-red-800 px-2"
                        title="Elimina"
                      >
                        🗑️
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal Nuova/Modifica Partita */}
      {showNuovaPartita && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b bg-gradient-to-r from-green-500 to-blue-600 text-white">
              <h3 className="text-xl font-bold">
                {editingPartita ? '✏️ Modifica Partita' : '⚽ Nuova Amichevole'}
              </h3>
              <p className="text-sm opacity-80">Registra una partita fuori torneo</p>
            </div>

            <div className="p-6 overflow-y-auto flex-1 space-y-4">
              {/* Data */}
              <div>
                <label className="block text-sm font-medium mb-1">📅 Data Partita</label>
                <input
                  type="date"
                  value={form.data_partita}
                  onChange={(e) => setForm({ ...form, data_partita: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                />
              </div>

              {/* Giocatori e Risultato */}
              <div className="grid grid-cols-5 gap-3 items-end">
                {/* Giocatore A */}
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1 text-green-700">🏃 Giocatore A</label>
                  <select
                    value={form.giocatore_a_id}
                    onChange={(e) => handleGiocatoreChange('a', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg bg-green-50"
                  >
                    <option value="">Seleziona...</option>
                    {soci.map(s => {
                      const nome = s.nome_completo || `${s.nome} ${s.cognome}`;
                      return <option key={s.id} value={s.id}>{nome}</option>;
                    })}
                  </select>
                  <input
                    type="text"
                    placeholder="Squadra (opz.)"
                    value={form.squadra_a}
                    onChange={(e) => setForm({ ...form, squadra_a: e.target.value })}
                    className="w-full mt-2 px-3 py-2 border rounded-lg text-sm"
                  />
                </div>

                {/* Risultato */}
                <div className="text-center">
                  <label className="block text-sm font-medium mb-1">⚽ Gol</label>
                  <div className="flex items-center justify-center gap-1">
                    <input
                      type="number"
                      min="0"
                      value={form.gol_a}
                      onChange={(e) => handleGolChange('gol_a', e.target.value)}
                      className="w-12 border rounded-lg px-2 py-2 text-center text-lg font-bold bg-green-50"
                    />
                    <span className="text-xl font-bold text-gray-400">-</span>
                    <input
                      type="number"
                      min="0"
                      value={form.gol_b}
                      onChange={(e) => handleGolChange('gol_b', e.target.value)}
                      className="w-12 border rounded-lg px-2 py-2 text-center text-lg font-bold bg-blue-50"
                    />
                  </div>
                </div>

                {/* Giocatore B */}
                <div className="col-span-2">
                  <label className="block text-sm font-medium mb-1 text-blue-700">🏃 Giocatore B</label>
                  <select
                    value={form.giocatore_b_id}
                    onChange={(e) => handleGiocatoreChange('b', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg bg-blue-50"
                  >
                    <option value="">Seleziona...</option>
                    {soci.map(s => {
                      const nome = s.nome_completo || `${s.nome} ${s.cognome}`;
                      return <option key={s.id} value={s.id}>{nome}</option>;
                    })}
                  </select>
                  <input
                    type="text"
                    placeholder="Squadra (opz.)"
                    value={form.squadra_b}
                    onChange={(e) => setForm({ ...form, squadra_b: e.target.value })}
                    className="w-full mt-2 px-3 py-2 border rounded-lg text-sm"
                  />
                </div>
              </div>

              {/* Punti Ranking */}
              <div className="bg-yellow-50 p-4 rounded-xl">
                <h4 className="font-medium text-yellow-800 mb-3">🏅 Punti Ranking (calcolati automaticamente)</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center">
                    <label className="block text-xs text-gray-500 mb-1">Giocatore A</label>
                    <input
                      type="number"
                      value={form.punti_ranking_a}
                      onChange={(e) => setForm({ ...form, punti_ranking_a: parseInt(e.target.value) || 0 })}
                      className="w-20 border rounded px-2 py-1 text-center font-bold text-green-600"
                    />
                  </div>
                  <div className="text-center">
                    <label className="block text-xs text-gray-500 mb-1">Giocatore B</label>
                    <input
                      type="number"
                      value={form.punti_ranking_b}
                      onChange={(e) => setForm({ ...form, punti_ranking_b: parseInt(e.target.value) || 0 })}
                      className="w-20 border rounded px-2 py-1 text-center font-bold text-blue-600"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 text-center mt-2">
                  Puoi modificare i punti manualmente se necessario
                </p>
              </div>

              {/* Note */}
              <div>
                <label className="block text-sm font-medium mb-1">📝 Note (opzionale)</label>
                <textarea
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg h-20"
                  placeholder="Note aggiuntive..."
                />
              </div>
            </div>

            <div className="p-6 border-t flex justify-end gap-3">
              <button
                onClick={() => { setShowNuovaPartita(false); setEditingPartita(null); }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                Annulla
              </button>
              <button
                onClick={savePartita}
                disabled={saving}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
                data-testid="partita-submit"
              >
                {saving ? 'Salvataggio...' : (editingPartita ? 'Aggiorna' : '✅ Salva Partita')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PartitaSingola;
